import {
  ThreadId,
  type OrchestrationEvent,
  type OrchestrationShellStreamEvent,
  type ServerConfig,
} from "@taskin-code/contracts";
import { defaultTerminalTitleForCliKind } from "@taskin-code/shared/terminalThreads";
import {
  Outlet,
  createRootRouteWithContext,
  type ErrorComponentProps,
  useNavigate,
  useParams,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { Throttler } from "@tanstack/react-pacer";

import { APP_DISPLAY_NAME } from "../branding";
import { useAuthStore } from "../auth/authStore";
import { ForceChangePassword } from "../auth/ForceChangePassword";
import { Button } from "../components/ui/button";
import { FullScreenLoadingState } from "../components/ui/loading-state";
import { AnchoredToastProvider, ToastProvider, toastManager } from "../components/ui/toast";
import { ArchiveHookToastBridge } from "../components/ArchiveHookToastBridge";
import { ConnectionStatusBanner } from "../components/ConnectionStatusBanner";
import { ProjectScriptRunToastBridge } from "../components/ProjectScriptRunToastBridge";
import { resolveAndPersistPreferredEditor } from "../editorPreferences";
import { serverConfigQueryOptions, serverQueryKeys } from "../lib/serverReactQuery";
import { readNativeApi } from "../nativeApi";
import { clearPromotedDraftThreads, useComposerDraftStore } from "../composerDraftStore";
import { useStore } from "../store";
import { useDevServerStore } from "../devServerStore";
import { useTerminalStateStore } from "../terminalStateStore";
import { terminalActivityFromEvent } from "../terminalActivity";
import {
  createThreadDeliveryTracker,
  planThreadGapRecovery,
  selectApplicableThreadEvents,
} from "../threadStreamDelivery";
import {
  onServerConfigUpdated,
  onServerProviderStatusesUpdated,
  onServerWelcome,
} from "../wsNativeApi";
import { providerQueryKeys } from "../lib/providerReactQuery";
import { projectQueryKeys } from "../lib/projectReactQuery";
import { collectActiveTerminalThreadIds } from "../lib/terminalStateCleanup";
import { TaskCompletionNotifications } from "../notifications/taskCompletion";
import { useWorkspaceStore, workspaceThreadId } from "../workspaceStore";
import { useAppTypography } from "../hooks/useAppTypography";
import { environmentQueryKeys } from "../lib/environmentQueries";
import { invalidateGitQueries } from "../lib/gitReactQuery";
import { parseDiffRouteSearch } from "../diffRouteSearch";
import { resolveSplitViewThreadIds, selectSplitView, useSplitViewStore } from "../splitViewStore";
import {
  resolveSubscribedThreadIds,
  useMountedPanelThreadIds,
} from "../panelThreadSubscriptionStore";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootRouteView,
  errorComponent: RootRouteErrorView,
  head: () => ({
    meta: [{ name: "title", content: APP_DISPLAY_NAME }],
  }),
});

function RootRouteView() {
  useAppTypography();

  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const authStatus = useAuthStore((state) => state.status);
  const hydrateAuth = useAuthStore((state) => state.hydrate);
  const mustChangePassword = useAuthStore((state) => state.user?.mustChangePassword ?? false);

  useEffect(() => {
    if (authStatus === "idle") {
      void hydrateAuth();
    }
  }, [authStatus, hydrateAuth]);

  // Redirect unauthenticated users to /login (except when already there).
  useEffect(() => {
    if (authStatus === "unauthenticated" && pathname !== "/login") {
      // The /login route is generated by the TanStack Router plugin at build/dev
      // time; cast to any to keep typecheck green when the route tree has not
      // yet been regenerated.
      void navigate({ to: "/login" as never, replace: true });
    }
  }, [authStatus, pathname, navigate]);

  // Login screen does not need a connected WS / native API.
  if (pathname === "/login") {
    return (
      <ToastProvider>
        <AnchoredToastProvider>
          <Outlet />
        </AnchoredToastProvider>
      </ToastProvider>
    );
  }

  if (authStatus === "idle" || authStatus === "loading") {
    return <FullScreenLoadingState label="Autenticando…" />;
  }

  if (authStatus === "unauthenticated") {
    // The redirect effect handles navigation; show a placeholder while it runs.
    return <FullScreenLoadingState label="Redirigiendo al inicio de sesión…" />;
  }

  // Force password change before any other interaction once authenticated.
  if (authStatus === "authenticated" && mustChangePassword) {
    return (
      <ToastProvider>
        <AnchoredToastProvider>
          <ForceChangePassword />
        </AnchoredToastProvider>
      </ToastProvider>
    );
  }

  if (!readNativeApi()) {
    return <FullScreenLoadingState label={`Conectando con el servidor de ${APP_DISPLAY_NAME}…`} />;
  }

  return (
    <ToastProvider>
      <AnchoredToastProvider>
        <EventRouter />
        <TaskCompletionNotifications />
        <DesktopProjectBootstrap />
        <ArchiveHookToastBridge />
        <ProjectScriptRunToastBridge />
        <ConnectionStatusBanner />
        <Outlet />
      </AnchoredToastProvider>
    </ToastProvider>
  );
}

function RootRouteErrorView({ error, reset }: ErrorComponentProps) {
  const message = errorMessage(error);
  const details = errorDetails(error);

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-red-500)_16%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {APP_DISPLAY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
          Something went wrong.
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => reset()}>
            Try again
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            Reload app
          </Button>
        </div>

        <details className="group mt-5 overflow-hidden rounded-lg border border-border/70 bg-background/55">
          <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-muted-foreground">
            <span className="group-open:hidden">Show error details</span>
            <span className="hidden group-open:inline">Hide error details</span>
          </summary>
          <pre className="max-h-56 overflow-auto border-t border-border/70 bg-background/80 px-3 py-2 text-xs text-foreground/85">
            {details}
          </pre>
        </details>
      </section>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return "An unexpected router error occurred.";
}

function errorDetails(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return "No additional error details are available.";
  }
}

function coalesceOrchestrationUiEvents(
  events: ReadonlyArray<OrchestrationEvent>,
): OrchestrationEvent[] {
  if (events.length < 2) {
    return [...events];
  }

  const coalesced: OrchestrationEvent[] = [];
  for (const event of events) {
    const previous = coalesced.at(-1);
    if (
      previous?.type === "thread.message-sent" &&
      event.type === "thread.message-sent" &&
      previous.payload.threadId === event.payload.threadId &&
      previous.payload.messageId === event.payload.messageId
    ) {
      coalesced[coalesced.length - 1] = {
        ...event,
        payload: {
          ...event.payload,
          attachments: event.payload.attachments ?? previous.payload.attachments,
          createdAt: previous.payload.createdAt,
          text:
            !event.payload.streaming && event.payload.text.length > 0
              ? event.payload.text
              : previous.payload.text + event.payload.text,
        },
      };
      continue;
    }

    coalesced.push(event);
  }

  return coalesced;
}

/** Backoff for re-requesting a shell snapshot that never arrived: 1 s, 2 s, 4 s… up to 30 s. */
const SHELL_RETRY_BASE_DELAY_MS = 1_000;
const SHELL_RETRY_MAX_DELAY_MS = 30_000;

function shouldFlushDomainEventImmediately(
  event: OrchestrationEvent,
  immediatelyFlushedAssistantMessageIds: Set<string>,
): boolean {
  if (event.type !== "thread.message-sent" || event.payload.role !== "assistant") {
    return false;
  }

  if (!event.payload.streaming) {
    immediatelyFlushedAssistantMessageIds.delete(event.payload.messageId);
    return false;
  }

  if (immediatelyFlushedAssistantMessageIds.has(event.payload.messageId)) {
    return false;
  }

  immediatelyFlushedAssistantMessageIds.add(event.payload.messageId);
  return true;
}

function EventRouter() {
  const syncServerShellSnapshot = useStore((store) => store.syncServerShellSnapshot);
  const syncServerThreadDetailHotPath = useStore((store) => store.syncServerThreadDetailHotPath);
  const applyShellEvent = useStore((store) => store.applyShellEvent);
  const applyThreadUserStates = useStore((store) => store.applyThreadUserStates);
  const applyOrchestrationEventsHotPath = useStore(
    (store) => store.applyOrchestrationEventsHotPath,
  );
  const setProjectExpanded = useStore((store) => store.setProjectExpanded);
  const removeOrphanedTerminalStates = useTerminalStateStore(
    (store) => store.removeOrphanedTerminalStates,
  );
  const setWorkspaceHomeDir = useWorkspaceStore((store) => store.setHomeDir);
  const workspacePages = useWorkspaceStore((store) => store.workspacePages);
  const queryClient = useQueryClient();
  const routeThreadId = useParams({
    strict: false,
    select: (params) => (params.threadId ? ThreadId.makeUnsafe(params.threadId) : null),
  });
  const routeSearch = useSearch({
    strict: false,
    select: (search) => parseDiffRouteSearch(search),
  });
  const activeSplitView = useSplitViewStore(selectSplitView(routeSearch.splitViewId ?? null));
  const mountedPanelThreadIds = useMountedPanelThreadIds();
  const visibleThreadIds = useMemo(() => {
    const routeThreadIds = activeSplitView
      ? resolveSplitViewThreadIds(activeSplitView)
      : routeThreadId
        ? [routeThreadId]
        : [];
    // A panel can be showing a thread the route knows nothing about, and its transcript needs the
    // same subscription the route's own thread gets.
    return resolveSubscribedThreadIds({ routeThreadIds, panelThreadIds: mountedPanelThreadIds });
  }, [activeSplitView, mountedPanelThreadIds, routeThreadId]);
  const visibleThreadIdsRef = useRef(visibleThreadIds);
  const reconcileThreadSubscriptionsRef = useRef<
    ((threadIds: readonly ThreadId[]) => Promise<void>) | null
  >(null);

  visibleThreadIdsRef.current = visibleThreadIds;

  useEffect(() => {
    const api = readNativeApi();
    if (!api) return;
    let disposed = false;
    let needsProviderInvalidation = false;
    let needsGitInvalidation = false;
    let needsEnvironmentInvalidation = false;
    let pendingDomainEvents: OrchestrationEvent[] = [];
    const immediatelyFlushedAssistantMessageIds = new Set<string>();
    let shellSnapshotSequence = -1;
    let pendingShellEvents: OrchestrationShellStreamEvent[] = [];
    const subscribedThreadIds = new Set<ThreadId>();
    const threadSnapshotSequenceById = new Map<ThreadId, number>();
    const pendingThreadEventsById = new Map<ThreadId, OrchestrationEvent[]>();
    const threadSnapshotRequestInFlight = new Set<ThreadId>();
    const threadReplayInFlight = new Set<ThreadId>();
    const threadDeliveryTracker = createThreadDeliveryTracker();
    let reconcileThreadSubscriptionsChain = Promise.resolve();

    const beginThreadSubscription = (threadId: ThreadId) => {
      threadSnapshotSequenceById.delete(threadId);
      pendingThreadEventsById.set(threadId, []);
      threadSnapshotRequestInFlight.delete(threadId);
      threadReplayInFlight.delete(threadId);
      threadDeliveryTracker.forget(threadId);
    };

    /**
     * Parks an event that cannot be applied yet.
     *
     * Dropping the cursor is the load-bearing half: with no cursor every later
     * push parks here too, so nothing is applied out of order behind the hole.
     */
    const parkThreadEvent = (threadId: ThreadId, event: OrchestrationEvent) => {
      threadSnapshotSequenceById.delete(threadId);
      const pendingThreadEvents = pendingThreadEventsById.get(threadId) ?? [];
      pendingThreadEvents.push(event);
      pendingThreadEventsById.set(threadId, pendingThreadEvents);
    };

    /**
     * Parks an event and makes sure a snapshot is on its way to close the hole.
     */
    const bufferThreadEventUntilSnapshot = (threadId: ThreadId, event: OrchestrationEvent) => {
      parkThreadEvent(threadId, event);
      // A replay already owns the recovery. Asking for a snapshot as well would
      // race it, and the snapshot would win by resetting the thread's history
      // window — throwing away exactly the loaded history the replay preserves.
      if (threadReplayInFlight.has(threadId)) {
        return;
      }
      if (subscribedThreadIds.has(threadId)) {
        void requestThreadSnapshot(threadId);
      }
    };

    /**
     * Close a proven push gap by replaying the thread's own events.
     *
     * Cheaper than the re-snapshot it replaces — a handful of events instead of
     * a full decode plus normalize — but the real reason is that a snapshot
     * resets the thread's history window, so a single lost push would discard
     * every older page the user had scrolled back to.
     *
     * Falls back to the snapshot whenever replay cannot be trusted to produce
     * the same state: no cursor to replay from, the request failed, or the
     * server reported the backlog is too large to be worth folding.
     */
    const recoverThreadFromGap = (threadId: ThreadId, event: OrchestrationEvent) => {
      // Read before parking: parking is what drops the cursor.
      const recovery = planThreadGapRecovery({
        cursor: threadSnapshotSequenceById.get(threadId),
        subscribed: subscribedThreadIds.has(threadId),
        replayInFlight: threadReplayInFlight.has(threadId),
      });
      parkThreadEvent(threadId, event);
      if (recovery.kind === "snapshot") {
        void requestThreadSnapshot(threadId);
        return;
      }
      if (recovery.kind === "replay") {
        void replayThreadFromSequence(threadId, recovery.fromSequenceExclusive);
      }
    };

    const replayThreadFromSequence = async (threadId: ThreadId, fromSequenceExclusive: number) => {
      threadReplayInFlight.add(threadId);
      let replayed = false;
      try {
        const result = await api.orchestration.replayThreadEvents({
          threadId,
          fromSequenceExclusive,
        });
        if (disposed) {
          return;
        }
        if (!result.snapshotRequired) {
          // Adopt the counter the server burned on this reply, exactly as a
          // snapshot's baseline is adopted. Without it the next push would have
          // to be taken on faith and a second lost push would go unnoticed.
          threadDeliveryTracker.observeSnapshot(threadId, result.deliverySequence);
          // Restore the cursor and hand the replayed events to the same buffer
          // the live pushes parked into, so one sorted pass applies both and
          // the sequence check drops whatever arrived twice.
          threadSnapshotSequenceById.set(threadId, fromSequenceExclusive);
          const parkedEvents = pendingThreadEventsById.get(threadId) ?? [];
          pendingThreadEventsById.set(threadId, [...parkedEvents, ...result.events]);
          flushThreadBuffer(threadId, fromSequenceExclusive);
          replayed = true;
        }
      } catch (error) {
        console.warn("thread event replay failed, falling back to a snapshot", {
          threadId,
          error,
        });
      } finally {
        threadReplayInFlight.delete(threadId);
      }
      if (replayed || disposed) {
        return;
      }
      if (subscribedThreadIds.has(threadId)) {
        void requestThreadSnapshot(threadId);
      }
    };

    /**
     * A subscription that fails silently is invisible and permanent: the thread
     * stays without a cursor, its events pile up in the pending buffer and are
     * never applied. Retry a couple of times with a short backoff and, if it
     * still fails, say so instead of pretending it worked.
     */
    const subscribeThreadWithRetry = async (threadId: ThreadId, attempts = 3): Promise<boolean> => {
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        if (disposed) {
          return false;
        }
        try {
          await api.orchestration.subscribeThread({ threadId });
          return true;
        } catch (error) {
          if (attempt === attempts) {
            console.error("failed to subscribe to thread events", { threadId, error });
            return false;
          }
          await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
        }
      }
      return false;
    };

    // Draft routes can subscribe before the server thread exists. Once the shell
    // row appears, explicitly request the first thread snapshot so buffered detail
    // events can flush instead of waiting forever.
    const requestThreadSnapshot = async (threadId: ThreadId) => {
      if (threadSnapshotSequenceById.has(threadId) || threadSnapshotRequestInFlight.has(threadId)) {
        return;
      }
      threadSnapshotRequestInFlight.add(threadId);
      try {
        // Keep the pending buffer intact on failure and retry on the next
        // shell/detail update.
        await subscribeThreadWithRetry(threadId);
      } finally {
        threadSnapshotRequestInFlight.delete(threadId);
      }
    };

    const flushThreadBuffer = (threadId: ThreadId, snapshotSequence: number) => {
      const pendingEvents = pendingThreadEventsById.get(threadId) ?? [];
      pendingThreadEventsById.delete(threadId);
      const cursor = threadSnapshotSequenceById.get(threadId) ?? snapshotSequence;
      for (const event of selectApplicableThreadEvents(pendingEvents, cursor)) {
        threadSnapshotSequenceById.set(threadId, event.sequence);
        queueDomainEvent(event);
      }
    };

    const flushShellBuffer = (snapshotSequence: number) => {
      const nextPending = pendingShellEvents
        .filter((event) => event.sequence > snapshotSequence)
        .toSorted((left, right) => left.sequence - right.sequence);
      pendingShellEvents = [];
      for (const event of nextPending) {
        shellSnapshotSequence = Math.max(shellSnapshotSequence, event.sequence);
        applyShellEvent(event);
      }
    };

    const reconcileThreadSubscriptions = async (threadIds: readonly ThreadId[]) => {
      const nextThreadIds = new Set(threadIds);
      const removals = [...subscribedThreadIds].filter((threadId) => !nextThreadIds.has(threadId));
      const additions = [...nextThreadIds].filter((threadId) => !subscribedThreadIds.has(threadId));

      for (const threadId of removals) {
        threadSnapshotSequenceById.delete(threadId);
        pendingThreadEventsById.delete(threadId);
        threadSnapshotRequestInFlight.delete(threadId);
        threadReplayInFlight.delete(threadId);
        threadDeliveryTracker.forget(threadId);
      }
      await Promise.all(
        removals.map((threadId) =>
          api.orchestration.unsubscribeThread({ threadId }).catch(() => undefined),
        ),
      );
      for (const threadId of additions) {
        beginThreadSubscription(threadId);
      }
      await Promise.all(additions.map((threadId) => subscribeThreadWithRetry(threadId)));

      subscribedThreadIds.clear();
      for (const threadId of nextThreadIds) {
        subscribedThreadIds.add(threadId);
      }
    };

    const enqueueThreadSubscriptionReconcile = (threadIds: readonly ThreadId[]) => {
      const nextThreadIds = [...threadIds];
      reconcileThreadSubscriptionsChain = reconcileThreadSubscriptionsChain
        .catch(() => undefined)
        .then(() => reconcileThreadSubscriptions(nextThreadIds));
      return reconcileThreadSubscriptionsChain;
    };

    // Bumped on every (re)subscription so a retry loop left over from an older
    // connection stops instead of racing the new one.
    let shellSubscriptionGeneration = 0;

    const subscribeShellOnce = () =>
      api.orchestration.subscribeShell().then(
        () => true,
        (error: unknown) => {
          console.error("failed to subscribe to the shell snapshot", { error });
          return false;
        },
      );

    /**
     * Keep asking for the shell until its snapshot lands.
     *
     * The sidebar and the project list hydrate from that one snapshot and have
     * no other way in. A request that failed — or outran the 60 s request
     * timeout — used to be swallowed, which left the sidebar spinning until a
     * full page reload. A slow request whose snapshot still arrives counts as
     * success: the push is the answer, not the response.
     */
    const retryShellSubscription = async (generation: number) => {
      for (let attempt = 1; ; attempt += 1) {
        await new Promise((resolve) =>
          setTimeout(
            resolve,
            Math.min(SHELL_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), SHELL_RETRY_MAX_DELAY_MS),
          ),
        );
        if (disposed || generation !== shellSubscriptionGeneration || shellSnapshotSequence >= 0) {
          return;
        }
        if (await subscribeShellOnce()) {
          return;
        }
      }
    };

    const ensureScopedSubscriptions = async () => {
      const generation = ++shellSubscriptionGeneration;
      shellSnapshotSequence = -1;
      pendingShellEvents = [];
      subscribedThreadIds.clear();
      threadSnapshotSequenceById.clear();
      pendingThreadEventsById.clear();
      threadReplayInFlight.clear();
      threadDeliveryTracker.clear();
      // Not awaited into a retry: thread subscriptions below must not wait on a
      // shell that is struggling — the open conversation can still stream.
      if (!(await subscribeShellOnce()) && shellSnapshotSequence < 0) {
        void retryShellSubscription(generation);
      }
      // After the shell, because it folds into threads the snapshot has just
      // put in the store — a state for a thread that is not there yet is
      // dropped. On its own promise rather than awaited into the chain below:
      // a sidebar that renders a moment before someone's settled threads dim
      // is a much smaller problem than a sidebar that waits for them.
      void api.orchestration
        .getThreadUserState()
        .then((snapshot) => applyThreadUserStates(snapshot.states))
        // Swallowed on purpose: an install with auth off answers with nothing,
        // and a failure here must not take the connection down with it. The
        // next reconnect asks again.
        .catch(() => undefined);
      await enqueueThreadSubscriptionReconcile(visibleThreadIdsRef.current);
    };

    const removeOrphanedTerminalsForCurrentState = () => {
      const draftThreadIds = Object.keys(
        useComposerDraftStore.getState().draftThreadsByThreadId,
      ) as ThreadId[];
      const activeThreadIds = collectActiveTerminalThreadIds({
        snapshotThreads: useStore.getState().threads.map((thread) => ({
          id: thread.id,
          deletedAt: null,
          archivedAt: thread.archivedAt ?? null,
        })),
        draftThreadIds,
        retainedThreadIds: workspacePages.map((workspace) => workspaceThreadId(workspace.id)),
      });
      removeOrphanedTerminalStates(activeThreadIds);
    };

    const flushPendingDomainEvents = () => {
      if (pendingDomainEvents.length > 0) {
        applyOrchestrationEventsHotPath(coalesceOrchestrationUiEvents(pendingDomainEvents));
        pendingDomainEvents = [];
      }
      if (needsProviderInvalidation) {
        needsProviderInvalidation = false;
        void queryClient.invalidateQueries({ queryKey: providerQueryKeys.all });
        // Invalidate workspace entry queries so the @-mention file picker
        // reflects files created, deleted, or restored during this turn.
        void queryClient.invalidateQueries({ queryKey: projectQueryKeys.all });
      }
      if (needsGitInvalidation) {
        needsGitInvalidation = false;
        void invalidateGitQueries(queryClient);
      }
      if (needsEnvironmentInvalidation) {
        needsEnvironmentInvalidation = false;
        void queryClient.invalidateQueries({ queryKey: environmentQueryKeys.all });
      }
    };

    const queueDomainEvent = (event: OrchestrationEvent) => {
      pendingDomainEvents.push(event);
      if (event.type === "thread.turn-diff-completed" || event.type === "thread.reverted") {
        needsProviderInvalidation = true;
      }
      // Environments change from outside the browser — the 10-minute sync
      // sweep, an agent running `sync-base`, somebody else creating one — so
      // the Entornos section and the header badge are refreshed from the event
      // rather than from a poll.
      //
      // Previews of a pull request ride the same rail: they are built and torn
      // down by an agent or by the expiry sweep, never by whoever is looking at
      // the card, and the list of them hangs off the environment they came from.
      if (event.type.startsWith("environment.") || event.type.startsWith("pr-preview.")) {
        needsEnvironmentInvalidation = true;
      }
      if (
        event.type === "thread.meta-updated" &&
        (event.payload.branch !== undefined ||
          event.payload.envMode !== undefined ||
          event.payload.worktreePath !== undefined ||
          event.payload.associatedWorktreePath !== undefined ||
          event.payload.associatedWorktreeBranch !== undefined ||
          event.payload.associatedWorktreeRef !== undefined)
      ) {
        needsGitInvalidation = true;
      }
      if (shouldFlushDomainEventImmediately(event, immediatelyFlushedAssistantMessageIds)) {
        domainEventFlushThrottler.cancel();
        flushPendingDomainEvents();
        return;
      }
      domainEventFlushThrottler.maybeExecute();
    };

    const domainEventFlushThrottler = new Throttler(
      () => {
        flushPendingDomainEvents();
      },
      {
        wait: 100,
        leading: false,
        trailing: true,
      },
    );

    reconcileThreadSubscriptionsRef.current = (threadIds) =>
      enqueueThreadSubscriptionReconcile(threadIds);

    const unsubShellEvent = api.orchestration.onShellEvent((item) => {
      if (item.kind === "snapshot") {
        shellSnapshotSequence = item.snapshot.snapshotSequence;
        syncServerShellSnapshot(item.snapshot);
        clearPromotedDraftThreads(new Set(item.snapshot.threads.map((thread) => thread.id)));
        removeOrphanedTerminalsForCurrentState();
        flushShellBuffer(item.snapshot.snapshotSequence);
        return;
      }

      if (shellSnapshotSequence < 0) {
        pendingShellEvents.push(item);
        return;
      }
      if (item.sequence <= shellSnapshotSequence) {
        return;
      }
      shellSnapshotSequence = item.sequence;
      applyShellEvent(item);
      if (item.kind === "thread-upserted") {
        clearPromotedDraftThreads(new Set([item.thread.id]));
      }
      if (
        item.kind === "thread-upserted" &&
        subscribedThreadIds.has(item.thread.id) &&
        !threadSnapshotSequenceById.has(item.thread.id)
      ) {
        void requestThreadSnapshot(item.thread.id);
      }
    });
    const unsubThreadEvent = api.orchestration.onThreadEvent((item) => {
      if (item.kind === "snapshot") {
        const threadId = item.snapshot.thread.id;
        threadDeliveryTracker.observeSnapshot(threadId, item.deliverySequence);
        threadSnapshotSequenceById.set(threadId, item.snapshot.snapshotSequence);
        threadSnapshotRequestInFlight.delete(threadId);
        syncServerThreadDetailHotPath(item.snapshot.thread, {
          hasMoreHistory: item.snapshot.hasMoreHistory,
          oldestCursor: item.snapshot.oldestCursor,
        });
        clearPromotedDraftThreads(new Set([threadId]));
        flushThreadBuffer(threadId, item.snapshot.snapshotSequence);
        return;
      }

      const threadId = ThreadId.makeUnsafe(String(item.event.aggregateId));
      const delivery = threadDeliveryTracker.observeEvent(threadId, item.deliverySequence);
      if (delivery === "gap") {
        // A lost push never comes back: the thread would silently miss a tool
        // call or the end of its turn forever. Replaying the thread's own
        // events closes the hole without discarding what is already on screen;
        // a snapshot is the fallback, not the first answer.
        recoverThreadFromGap(threadId, item.event);
        return;
      }

      const latestThreadSequence = threadSnapshotSequenceById.get(threadId);
      if (latestThreadSequence === undefined) {
        bufferThreadEventUntilSnapshot(threadId, item.event);
        return;
      }
      if (item.event.sequence <= latestThreadSequence) {
        return;
      }
      threadSnapshotSequenceById.set(threadId, item.event.sequence);
      queueDomainEvent(item.event);
    });
    // Dev server transitions are runtime state, not domain events, so they land
    // straight in their own store instead of going through the event queue.
    const unsubDevServerStatus = api.orchestration.onDevServerStatusChanged((payload) => {
      useDevServerStore.getState().applyStatusChange(payload.threadId, payload.status);
    });
    const unsubTerminalEvent = api.terminal.onEvent((event) => {
      const terminalThreadId = ThreadId.makeUnsafe(event.threadId);
      if (event.type === "activity") {
        if (event.cliKind) {
          useTerminalStateStore.getState().setTerminalMetadata(terminalThreadId, event.terminalId, {
            cliKind: event.cliKind,
            label: defaultTerminalTitleForCliKind(event.cliKind),
          });
        }
      }
      const activity = terminalActivityFromEvent(event);
      if (activity === null) {
        return;
      }
      useTerminalStateStore.getState().setTerminalActivity(terminalThreadId, event.terminalId, {
        hasRunningSubprocess: activity.hasRunningSubprocess,
        agentState: activity.agentState,
      });
    });
    const unsubWelcome = onServerWelcome((payload) => {
      void (async () => {
        setWorkspaceHomeDir(payload.homeDir);
        await ensureScopedSubscriptions();
        if (disposed) {
          return;
        }

        // The welcome used to also navigate `/` onto its bootstrap thread. `/`
        // is now the home page, and a screen the user is meant to land on
        // cannot be one the first server message walks away from. The project
        // is still expanded so the thread is one click away in the sidebar.
        if (!payload.bootstrapProjectId) {
          return;
        }
        setProjectExpanded(payload.bootstrapProjectId, true);
      })().catch(() => undefined);
    });
    // onServerConfigUpdated replays the latest cached value synchronously
    // during subscribe. Skip the toast for that replay so effect re-runs
    // don't produce duplicate toasts.
    let subscribed = false;
    const unsubServerConfigUpdated = onServerConfigUpdated((payload) => {
      void queryClient.invalidateQueries({ queryKey: serverQueryKeys.config() });
      if (!subscribed) return;
      const issue = payload.issues.find((entry) => entry.kind.startsWith("keybindings."));
      if (!issue) {
        toastManager.add({
          type: "success",
          title: "Keybindings updated",
          description: "Keybindings configuration reloaded successfully.",
        });
        return;
      }

      toastManager.add({
        type: "warning",
        title: "Invalid keybindings configuration",
        description: issue.message,
        actionProps: {
          children: "Open keybindings.json",
          onClick: () => {
            void queryClient
              .ensureQueryData(serverConfigQueryOptions())
              .then((config) => {
                const editor = resolveAndPersistPreferredEditor(config.availableEditors);
                if (!editor) {
                  throw new Error("No available editors found.");
                }
                return api.shell.openInEditor(config.keybindingsConfigPath, editor);
              })
              .catch((error) => {
                toastManager.add({
                  type: "error",
                  title: "Unable to open keybindings file",
                  description:
                    error instanceof Error ? error.message : "Unknown error opening file.",
                });
              });
          },
        },
      });
    });
    const unsubProviderStatusesUpdated = onServerProviderStatusesUpdated((payload) => {
      const currentConfig = queryClient.getQueryData<ServerConfig>(serverQueryKeys.config());
      if (!currentConfig) {
        void queryClient.fetchQuery(serverConfigQueryOptions()).catch(() => undefined);
        return;
      }
      queryClient.setQueryData(serverQueryKeys.config(), {
        ...currentConfig,
        providers: payload.providers,
      });
    });
    subscribed = true;
    void ensureScopedSubscriptions();

    return () => {
      flushPendingDomainEvents();
      disposed = true;
      needsProviderInvalidation = false;
      domainEventFlushThrottler.cancel();
      reconcileThreadSubscriptionsRef.current = null;
      void api.orchestration.unsubscribeShell().catch(() => undefined);
      void Promise.all(
        [...subscribedThreadIds].map((threadId) =>
          api.orchestration.unsubscribeThread({ threadId }).catch(() => undefined),
        ),
      );
      unsubShellEvent();
      unsubThreadEvent();
      unsubDevServerStatus();
      unsubTerminalEvent();
      unsubWelcome();
      unsubServerConfigUpdated();
      unsubProviderStatusesUpdated();
    };
  }, [
    applyOrchestrationEventsHotPath,
    applyShellEvent,
    applyThreadUserStates,
    queryClient,
    removeOrphanedTerminalStates,
    setProjectExpanded,
    setWorkspaceHomeDir,
    syncServerShellSnapshot,
    syncServerThreadDetailHotPath,
    workspacePages,
  ]);

  useEffect(() => {
    const reconcile = reconcileThreadSubscriptionsRef.current;
    if (!reconcile) {
      return;
    }
    void reconcile(visibleThreadIds);
  }, [visibleThreadIds]);

  return null;
}

function DesktopProjectBootstrap() {
  // Desktop hydration runs through EventRouter project + orchestration sync.
  return null;
}
